import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import KitOptInForm from "@/components/KitOptInForm";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { trackClick, trackEvent } from "@/utils/posthog";
import { useTrackScrollDepth } from "@/hooks/use-posthog";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { useEffect } from "react";
import { library, GENRE_LABEL } from "@/data/aiWorkflows";
import { LIBRARY_HELP_COPY } from "@/data/promptLibraryCopy";
import { buildOrganizationJourneyUrl } from "@/utils/organizationJourney";

const WORKFLOW_CONTENTS = [
  { title: "What to bring", description: "The notes, documents, and task details you'll need." },
  { title: "Instructions to copy", description: "Instructions you can adapt to your work and save for next time." },
  { title: "An example to try", description: "A sample prompt to help you get a useful first draft." },
  { title: "What to check", description: "A list of things to review against your sources before you use or share the draft." },
];

const PREVIEW_SLUGS = [
  "meeting-decisions-action-items",
  "content-repurposing",
  "sop-builder",
];
const previewWorkflows = PREVIEW_SLUGS.map((slug) => library.find((entry) => entry.slug === slug)).filter((entry) => entry !== undefined);

const Library = () => {
  useTrackScrollDepth("Library Landing Page");
  const search = useHydratedSearch();
  const destination = (pathname: string, location: string) => buildOrganizationJourneyUrl(pathname, search, { source: "library", location, audience: "organization" });
  const helpHref = destination("/ai-30", "hero_explainer");

  useEffect(() => {
    trackEvent("Library Landing Page View", {
      page_type: "lead_capture",
      offer: "fortune_100_prompt_library",
    });
  }, []);

  return (
    <div className="min-h-screen bg-longhand-paper text-longhand-ink">
      <SEO
        title={`Free Fortune 100 AI Prompt Library - ${library.length} Practical Workflows`}
        description={`Pick a task you already do. Get ${library.length} free AI workflows with instructions to copy, examples to try, and a clear list of things to check.`}
        keywords={["AI workflows", "AI prompts", "fortune 100 prompts", "ChatGPT prompts", "Claude skills", "prompt templates"]}
      />
      <Navigation hideCTA logoClassName="h-5 w-auto sm:h-10">
        <Button asChild className="min-h-11 bg-longhand-accent text-longhand-paper hover:bg-longhand-accent-hover">
          <a href="#library-signup">Get the library</a>
        </Button>
      </Navigation>

      <main>
        <section
          aria-labelledby="library-signup-title"
          className="relative isolate overflow-hidden bg-background px-4 pb-8 pt-24 text-foreground sm:px-6 sm:pb-12 sm:pt-28 lg:px-8"
        >
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
            <img src="/ai-in-ar.png" alt="" className="h-full w-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-background/70" />
          </div>
          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-x-16">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-widest text-blue-300">
                The Fortune 100 AI Prompt Library™
              </p>
              <h1 id="library-signup-title" className="mt-4 max-w-3xl text-balance font-serif text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                Put AI to work on{" "}
                <span className="text-blue-300">something you already do.</span>
              </h1>
              <p className="mt-4 max-w-2xl text-lg leading-relaxed text-foreground/80">
                Get {library.length} practical AI workflows for leaders and teams.
                Pick a task, copy the instructions, and try them with your own
                notes or documents. Check the draft before you use it.
              </p>
              <a href="#library-preview" className="mt-4 inline-flex min-h-11 items-center gap-2 font-semibold text-blue-300 underline underline-offset-4 hover:text-blue-200">
                See what&apos;s inside
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>

            <Card id="library-signup" tabIndex={-1} className="min-w-0 scroll-mt-24 border-foreground/20 bg-background/90 p-6 text-foreground sm:p-8 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center">
              <p className="text-sm font-semibold text-blue-300">Free library access</p>
              <h2 className="mt-2 font-serif text-2xl font-semibold sm:text-3xl">Get the library link</h2>
              <p className="mb-6 mt-3 leading-relaxed text-foreground/80">
                Leave your email and we&apos;ll send you the library link. You can search
                by task or team and come back whenever you need it.
              </p>
              <KitOptInForm
                uid="fb2b5743bc"
                conversionName="Fortune 100 Library Signup"
                location="library_landing_page"
                confirmationPath="/library-confirmed"
                fallbackHref="https://techleaders.kit.com/fb2b5743bc"
                fallbackLabel="Sign up for the free library"
                className="min-h-40"
                submitLabel="Send me the library"
                firstNameOptional
                showFieldLabels
                journeyContext={{ audience: "organization" }}
              />
              <p className="mt-4 text-sm leading-relaxed text-foreground/80">
                Check your inbox in case you need to confirm your email.
                Unsubscribe any time.
              </p>
            </Card>

            <aside aria-labelledby="library-help-title" className="min-w-0 border-t border-foreground/20 pt-6 lg:col-start-1 lg:row-start-2">
              <h2 id="library-help-title" className="font-serif text-xl font-semibold sm:text-2xl">{LIBRARY_HELP_COPY.heading}</h2>
              <p className="mt-3 text-base leading-relaxed text-foreground/80">{LIBRARY_HELP_COPY.description}</p>
              <p className="mt-3 text-base leading-relaxed text-foreground/80">{LIBRARY_HELP_COPY.invitation}</p>
              <a
                href={helpHref}
                className="mt-3 inline-flex min-h-11 items-center gap-2 font-semibold text-blue-300 underline underline-offset-4 hover:text-blue-200"
                onClick={() => trackClick("See How We Help - Library Landing Page", { location: "hero_explainer", destination: helpHref })}
              >
                See how we help <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </aside>
          </div>
        </section>

        <section id="library-preview" className="scroll-mt-24 border-y border-longhand-border px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <p className="text-xs font-bold uppercase tracking-widest text-longhand-accent">Inside every workflow</p>
            <h2 className="mt-3 font-serif text-3xl font-semibold sm:text-4xl">Here&apos;s what you get.</h2>
            <div className="mt-9 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {WORKFLOW_CONTENTS.map((item, index) => (
                <div key={item.title}>
                  <p className="text-sm font-semibold text-longhand-accent">0{index + 1}</p>
                  <h3 className="mt-2 text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-longhand-ink/80">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-widest text-longhand-accent">A few places to start</p>
              <h2 className="mt-3 font-serif text-3xl font-semibold sm:text-4xl">Pick something you need to do today.</h2>
              <p className="mt-4 text-lg leading-relaxed text-longhand-ink/80">
                You&apos;ll find workflows for sales, marketing, operations,
                finance, product, people, and more. Sign up above to access the
                full instructions for these examples and the rest of the library.
              </p>
            </div>
            <div className="mt-9 grid gap-5 md:grid-cols-3">
              {previewWorkflows.map((entry) => (
                <a key={entry.slug} href="#library-signup"
                  className="group flex flex-col rounded-xl border border-longhand-border p-6 transition-colors hover:bg-longhand-accent-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-longhand-accent"
                  onClick={() => {
                    trackClick("Get Workflow Access - Library Landing Page", { location: "workflow_preview", workflow: entry.slug, destination: "#library-signup" });
                  }}>
                  <p className="text-xs font-bold uppercase tracking-widest text-longhand-accent">{GENRE_LABEL[entry.category.id]}</p>
                  <h3 className="mt-3 text-xl font-semibold">{entry.title}</h3>
                  <p className="mb-6 mt-3 text-sm leading-relaxed text-longhand-ink/80">{entry.purpose}</p>
                  <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold">
                    Get free access
                    <ArrowRight className="h-4 w-4 transition-transform motion-safe:group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-longhand-border px-4 py-12 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-2 lg:items-center lg:gap-16">
            <div>
              <h2 className="font-serif text-3xl font-semibold">Try it with your own work.</h2>
              <p className="mt-4 leading-relaxed text-longhand-ink/80">
                Pick a task you know well. Copy the instructions into your AI
                tool, add the notes or documents it asks for, and try the sample
                prompt. Check the draft against your sources before you use it.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-longhand-ink/80">
                You don&apos;t need a technical background. The starter tasks
                cover meeting notes, email drafts, project updates, and writing
                down how a task gets done. Some advanced workflows need Claude
                and additional setup.
              </p>
            </div>
            <div className="lg:justify-self-end">
              <Button asChild size="lg" className="h-auto w-full whitespace-normal bg-longhand-accent px-8 py-4 text-longhand-paper hover:bg-longhand-accent-hover sm:w-auto">
                <a href="#library-signup" onClick={() => trackClick("Get Library Link - Library Landing Page", { location: "bottom_cta" })}>
                  Get the free library link
                  <ArrowRight className="ml-2 h-5 w-5 shrink-0" aria-hidden="true" />
                </a>
              </Button>
              <p className="mt-3 text-sm text-longhand-ink/80">Try one useful task. Save the instructions for next time.</p>
            </div>
          </div>
          <p className="mx-auto mt-10 max-w-6xl text-xs leading-relaxed text-longhand-ink/80">
            “Fortune 100” refers to the company list. The library includes
            templates we&apos;ve created at Technical Leaders. The name doesn&apos;t
            imply endorsement by those companies.
          </p>
        </section>
      </main>

      <div className="text-foreground"><Footer /></div>
    </div>
  );
};

export default Library;
